<template>
  <header
    class="sticky top-0 z-50 w-full bg-white/95 backdrop-blur-sm border-b border-t1-border"
    role="banner"
  >
    <div class="max-w-6xl mx-auto px-4 sm:px-6 h-20 flex items-center justify-between">
      <div class="flex min-w-0 flex-1 items-center">
        <span class="text-xl font-bold tracking-tight uppercase text-t1-neutral-dark truncate">{{ name || 'Restaurant' }}</span>
      </div>
      <nav class="hidden md:flex items-center gap-10">
        <a class="text-sm font-medium min-h-[44px] flex cursor-pointer items-center rounded transition-colors duration-200 hover:text-t1-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary focus-visible:ring-offset-2" href="#menu">{{ navLabel || viewListLabel || $t('public.t1NavMenu') }}</a>
        <a v-if="feedbackAvailable" class="text-sm font-medium min-h-[44px] flex cursor-pointer items-center rounded transition-colors duration-200 hover:text-t1-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary focus-visible:ring-offset-2" href="#reviews">{{ $t('public.reviewsNav') }}</a>
        <div v-else-if="isOwnerViewer && ownerViewMode" class="py-2 px-3 rounded border border-dashed border-t1-neutral-dark/40 bg-t1-neutral-dark/5 max-w-[140px]">
          <p class="text-xs font-mono text-t1-neutral-dark/70 break-words whitespace-normal">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
        </div>
        <a class="text-sm font-medium min-h-[44px] flex cursor-pointer items-center rounded transition-colors duration-200 hover:text-t1-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary focus-visible:ring-offset-2" href="#contact">{{ $t('public.contactNav') }}</a>
        <PublicReservationTrigger
          v-if="reservationAvailable"
          :slug="slug"
          template="template-1"
          :primary-color="primaryColor"
          :button-label="reserveButtonLabel"
          :modal-title="reserveModalHeading"
          :pax-label="reservePaxLabel"
        >
          <template #button="{ openModal }">
            <button
              type="button"
              class="rms-t1-reservation-btn flex min-h-[44px] cursor-pointer items-center rounded px-6 py-2.5 text-sm font-bold tracking-wide text-white transition-opacity duration-200 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              :class="primaryColor ? 'focus-visible:ring-[var(--rms-t1-btn-primary)]' : 'bg-t1-primary hover:bg-t1-primary/90 focus-visible:ring-t1-primary'"
              :style="reservationButtonStyle"
              :aria-label="reserveButtonLabel"
              data-testid="reservation-open-modal"
              @click="openModal"
            >
              {{ reserveButtonLabel }}
            </button>
          </template>
        </PublicReservationTrigger>
        <div
          v-else-if="isOwnerViewer && ownerViewMode"
          class="py-2 px-3 rounded border border-dashed border-t1-neutral-dark/40 bg-t1-neutral-dark/5 max-w-[140px]"
        >
          <p class="text-xs font-mono text-t1-neutral-dark/70 break-words whitespace-normal">{{ $t('public.ownerNavBookingUnavailable') }}</p>
        </div>
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="text-t1-neutral-dark border-t1-border"
          @select-locale="$emit('select-locale', $event)"
        />
      </nav>
      <div class="flex items-center gap-2 md:hidden">
        <LanguageDropdown
          :languages="languages"
          :current-locale="currentLocale"
          class="rms-language-dropdown--header-mobile text-t1-neutral-dark border-t1-border"
          @select-locale="$emit('select-locale', $event)"
        />
        <button
          type="button"
          class="flex min-h-[44px] min-w-[44px] cursor-pointer items-center justify-center rounded p-2 transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary focus-visible:ring-offset-2"
          :aria-label="mobileNavOpen ? $t('app.close') : (viewListAriaLabel || $t('public.t1NavMenu'))"
          :aria-expanded="mobileNavOpen"
          data-testid="header-mobile-menu-btn"
          @click="mobileNavOpen = !mobileNavOpen"
        >
          <span class="material-symbols-outlined text-t1-neutral-dark">{{ mobileNavOpen ? 'close' : 'menu' }}</span>
        </button>
      </div>
    </div>
    <!-- Mobile nav panel -->
    <Transition name="rms-mobile-nav">
      <div
        v-if="mobileNavOpen"
        class="fixed inset-0 top-20 z-40 md:hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        <div class="absolute inset-0 bg-black/40" aria-hidden="true" @click="mobileNavOpen = false" />
        <nav class="absolute left-0 right-0 top-0 bg-white dark:bg-zinc-900 border-b border-t1-border shadow-lg flex flex-col py-4 px-4 gap-1" aria-label="Main">
          <a class="flex min-h-[44px] cursor-pointer items-center rounded-lg px-4 text-base font-medium text-t1-neutral-dark transition-colors duration-200 hover:bg-t1-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary" href="#menu" @click="mobileNavOpen = false">{{ navLabel || viewListLabel || $t('public.t1NavMenu') }}</a>
          <a v-if="feedbackAvailable" class="flex min-h-[44px] cursor-pointer items-center rounded-lg px-4 text-base font-medium text-t1-neutral-dark transition-colors duration-200 hover:bg-t1-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary" href="#reviews" @click="mobileNavOpen = false">{{ $t('public.reviewsNav') }}</a>
          <div v-else-if="isOwnerViewer && ownerViewMode" class="py-3 px-3 rounded border border-dashed border-t1-neutral-dark/40 bg-t1-neutral-dark/5" @click="mobileNavOpen = false">
            <p class="text-xs font-mono text-t1-neutral-dark/70">{{ $t('public.ownerNavReviewsUnavailable') }}</p>
          </div>
          <a class="flex min-h-[44px] cursor-pointer items-center rounded-lg px-4 text-base font-medium text-t1-neutral-dark transition-colors duration-200 hover:bg-t1-bg focus:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary" href="#contact" @click="mobileNavOpen = false">{{ $t('public.contactNav') }}</a>
          <PublicReservationTrigger
            v-if="reservationAvailable"
            :slug="slug"
            template="template-1"
            :primary-color="primaryColor"
            :button-label="reserveButtonLabel"
            :modal-title="reserveModalHeading"
            :pax-label="reservePaxLabel"
          >
            <template #button="{ openModal }">
              <button
                type="button"
                class="rms-t1-reservation-btn flex min-h-[44px] w-full cursor-pointer items-center rounded-lg px-4 text-left text-base font-medium text-white transition-opacity duration-200 hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                :class="primaryColor ? 'focus-visible:ring-[var(--rms-t1-btn-primary)]' : 'bg-t1-primary hover:bg-t1-primary/90 focus-visible:ring-t1-primary'"
                :style="reservationButtonStyle"
                :aria-label="reserveButtonLabel"
                data-testid="reservation-open-modal-mobile"
                @click="openModal()"
              >
                {{ reserveButtonLabel }}
              </button>
            </template>
          </PublicReservationTrigger>
          <div
            v-else-if="isOwnerViewer && ownerViewMode"
            class="py-3 px-3 rounded border border-dashed border-t1-neutral-dark/40 bg-t1-neutral-dark/5"
            @click="mobileNavOpen = false"
          >
            <p class="text-xs font-mono text-t1-neutral-dark/70">{{ $t('public.ownerNavBookingUnavailable') }}</p>
          </div>
        </nav>
      </div>
    </Transition>
    <div
      v-if="showOwnerNeedsDataNotice"
      class="max-w-6xl mx-auto px-4 sm:px-6 py-3"
      role="status"
      aria-live="polite"
    >
      <div class="py-4 px-4 rounded border border-dashed border-t1-border bg-t1-bg/80">
        <p class="text-sm font-mono text-t1-neutral-dark/80">{{ $t('public.ownerNeedsDataIntro') }}</p>
        <a
          v-if="ownerAdminUrl"
          :href="ownerAdminUrl"
          class="mt-2 inline-flex min-h-[44px] cursor-pointer items-center rounded text-sm font-mono text-t1-neutral-dark/80 underline underline-offset-2 transition-colors duration-200 hover:text-t1-neutral-dark focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-t1-primary focus-visible:ring-offset-1"
          :aria-label="$t('public.openAdminPage')"
        >
          {{ $t('public.openAdminPage') }}
        </a>
      </div>
    </div>
  </header>
</template>

<script setup>
import { ref, computed } from 'vue'
import LanguageDropdown from '@/components/public/LanguageDropdown.vue'
import PublicReservationTrigger from '@/components/public/PublicReservationTrigger.vue'

const mobileNavOpen = ref(false)

const props = defineProps({
  name: { type: String, default: '' },
  viewer: { type: Object, default: () => ({ is_owner: false, owner_admin_url: null, needs_data: false }) },
  /** When false, owner-only notice is hidden (e.g. owner toggled "view as customer"). */
  ownerViewMode: { type: Boolean, default: true },
  languages: { type: Array, default: () => [] },
  currentLocale: { type: String, default: '' },
  /** Show "Book a Table" in header when true (matches generic-templates). */
  reservationAvailable: { type: Boolean, default: false },
  slug: { type: String, default: '' },
  primaryColor: { type: String, default: null },
  viewListLabel: { type: String, default: '' },
  navLabel: { type: String, default: '' },
  viewListAriaLabel: { type: String, default: '' },
  reserveButtonLabel: { type: String, default: '' },
  reserveModalHeading: { type: String, default: '' },
  reservePaxLabel: { type: String, default: '' },
  feedbackAvailable: { type: Boolean, default: false },
})

/** Only set when restaurant has a custom primary color; otherwise class bg-t1-primary applies. */
const reservationButtonStyle = computed(() => {
  const color = props.primaryColor?.trim()
  if (!color) return undefined
  return {
    backgroundColor: color,
    ['--rms-t1-btn-primary']: color,
  }
})

const isOwnerViewer = computed(() => props.viewer?.is_owner === true)
const ownerAdminUrl = computed(() => {
  const url = props.viewer?.owner_admin_url
  return typeof url === 'string' && url.trim() !== '' ? url : null
})
/** Show "needs data" notice only when owner is viewing and site needs data and not in "view as customer" mode. */
const showOwnerNeedsDataNotice = computed(() =>
  props.ownerViewMode && isOwnerViewer.value && props.viewer?.needs_data === true
)

defineEmits(['select-locale'])
</script>

<style scoped>
.rms-t1-reservation-btn:hover {
  opacity: 0.9;
}
.rms-mobile-nav-enter-active,
.rms-mobile-nav-leave-active {
  transition: opacity 0.2s ease;
}
.rms-mobile-nav-enter-active nav,
.rms-mobile-nav-leave-active nav {
  transition: transform 0.2s ease;
}
.rms-mobile-nav-enter-from,
.rms-mobile-nav-leave-to {
  opacity: 0;
}
.rms-mobile-nav-enter-from nav,
.rms-mobile-nav-leave-to nav {
  transform: translateY(-8px);
}
</style>
